import { Loader, ScrollArea } from "@mantine/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { JSX, ReactNode } from "react";
import { ComponentProps, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { FiAlertTriangle } from "react-icons/fi";

import { SearchInput } from "@/components/@custom/SearchInput";
import { omitClassNameFromProps } from "@/utils";
import { cn } from "@/utils/cn";

type ExpeditionQueueStatusBaseProps<T> = {
  loading: boolean;
  slots: {
    search: { props: ComponentProps<typeof SearchInput>; disable?: boolean };
    loader?: { props: ComponentProps<typeof Loader>; wrapper?: ComponentProps<"div"> };
    section?: { props: ComponentProps<"section"> };
    empty?: {
      props: ComponentProps<"div">;
      message?: string;
    };
    alert?: {
      props?: ComponentProps<"div">;
      message: string | ReactNode;
    };
  };
  list: {
    data: T[];
    render: (vehicles: T, index: number, arr: T[]) => ReactNode;
    Wrapper?: (children: ReactNode) => ReactNode;
    filter?: (vehicles: T) => void;
    virtualized?: boolean;
  };
};

const GAP = 4; // gap-1 = 4px

function ExpeditionQueueStatusBaseImpl<T>({ slots, loading, list }: ExpeditionQueueStatusBaseProps<T>) {
  const isVirtualized = list.virtualized ?? true;

  const isFirstRender = useRef(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const newData = useMemo(
    () => list.data.filter(list.filter || (() => true)),

    [list.data, list.filter]
  ) as T[];

  const estimateSize = useCallback(() => 200 + GAP, []);
  const measureElement = useCallback((el: Element) => el.getBoundingClientRect().height, []);

  const rowVirtualizer = useVirtualizer({
    count: newData.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize,
    measureElement,
    overscan: 5,
    useFlushSync: false
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  return (
    <div className="flex flex-col gap-2 has-[>.sticky]:relative">
      {!slots.search.disable && <SearchInput {...slots.search.props} />}

      {slots.alert && (
        <div
          className={cn("flex w-full max-w-64 items-center justify-center gap-2 self-center rounded-full bg-yellow-100 px-4 py-1.5 text-xs", slots.alert.props?.className)}
          {...omitClassNameFromProps(slots.alert?.props ?? {})}
        >
          <FiAlertTriangle size={24} fill="#FFDF6C" />

          <div>{slots.alert.message}</div>
        </div>
      )}

      {!loading && (
        <section {...slots?.section?.props}>
          {newData?.length > 0 && (
            <>
              {!isVirtualized && (
                <div className="flex flex-col gap-4">
                  {list.Wrapper && list.render && list.Wrapper(newData.map(list.render))}
                  {!list.Wrapper && list.render && newData.map(list.render)}
                </div>
              )}

              {isVirtualized && (
                <ScrollArea.Autosize h={400} viewportRef={scrollContainerRef}>
                  <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: "relative" }}>
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                      <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          paddingBottom: `${GAP}px`,
                          transform: `translateY(${virtualRow.start}px)`
                        }}
                      >
                        {list.Wrapper && list.Wrapper(list.render(newData[virtualRow.index], virtualRow.index, newData))}
                        {!list.Wrapper && list.render(newData[virtualRow.index], virtualRow.index, newData)}
                      </div>
                    ))}
                  </div>
                </ScrollArea.Autosize>
              )}
            </>
          )}

          {newData?.length === 0 && !isFirstRender.current && (
            <div {...slots?.empty?.props}>
              <p className="text-center">{slots?.empty?.message || "Nenhum veículo encontrado."}</p>
            </div>
          )}
        </section>
      )}

      {loading && (
        <div className="flex items-center justify-center" {...slots?.loader?.wrapper}>
          <Loader type="dots" {...slots?.loader?.props} />
        </div>
      )}
    </div>
  );
}

export const ExpeditionQueueStatusBase = memo(ExpeditionQueueStatusBaseImpl) as <T>(props: ExpeditionQueueStatusBaseProps<T>) => JSX.Element;
// Configurar displayName para dev tools
Object.defineProperty(ExpeditionQueueStatusBase, "displayName", {
  value: "ExpeditionQueueStatusBase",
  writable: false,
  enumerable: false,
  configurable: true
});
